import React, { useState } from 'react';
import i18next from 'i18next';
import { ConfirmModal } from 'components';
import { ResolveConflictLabel } from 'icons';

export enum KeepType {
  Local = 'KeepLocal',
  Cloud = 'KeepCloud',
}

export type CadRefType = React.MutableRefObject<{
  closeConfirmModal: () => void;
}>;

type ResolveConflictParams = {
  loading?: boolean;
  filename?: string;
  numBulkFiles?: number;
  onKeepCloud: () => Promise<void>;
  onKeepLocal: () => Promise<void>;
};

export const useResolveConflict = ({
  loading: overrideLoading,
  filename,
  numBulkFiles = 0,
  onKeepCloud,
  onKeepLocal,
}: ResolveConflictParams) => {
  const [keepType, setKeepType] = useState<KeepType>(KeepType.Local);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [innerLoading, setInnerLoading] = useState<boolean>(false);

  const isSelectedKeepLocal = keepType === KeepType.Local;
  const closeConfirmModal = () => setIsOpen(false);

  const openConfirmModal = (type = KeepType.Local) => {
    setKeepType(type);
    setIsOpen(true);
  };

  const keepTypeMap = {
    [KeepType.Local]: onKeepLocal,
    [KeepType.Cloud]: onKeepCloud,
  };

  const isBulkResolution = numBulkFiles > 0;

  let title = '';
  let subtitle1 = '';
  let subtitle2 = '';
  let text = '';

  if (isBulkResolution) {
    title = i18next.t('Resolve All Conflicts');

    subtitle1 = `${numBulkFiles} Files`;
    subtitle2 = `(${i18next.t(
      isSelectedKeepLocal ? 'Keep All Local' : 'Keep All Cloud',
    )})`;

    text = isSelectedKeepLocal
      ? i18next.t(
          'This will ignore conflict with the Cloud Drive and keep all your local changes',
        )
      : i18next.t(
          'This will override all your local files with the contents from the Cloud Drive',
        );
  } else {
    title = i18next.t('Resolve Conflicts');

    subtitle1 = filename;
    subtitle2 = `(${i18next.t(
      isSelectedKeepLocal ? 'Keep Local' : 'Keep Cloud',
    )})`;

    text = isSelectedKeepLocal
      ? i18next.t(
          'This will ignore conflict with the Cloud Drive and keep your local changes',
        )
      : i18next.t(
          'This will override your local file with the content from the Cloud Drive',
        );
  }

  let loading = innerLoading;
  if (typeof overrideLoading === 'boolean') loading = overrideLoading;

  return {
    setKeepType,
    openConfirmModal,
    closeConfirmModal,
    confirmModal: (
      <ConfirmModal
        open={isOpen}
        loading={loading}
        content={
          <div className='flex flex-col justify-center items-center'>
            <ResolveConflictLabel />
            <h2 className=' mt-4'>{title}</h2>

            <div className=' text-wfBlack my-2 w-full'>
              <div className=' flex justify-center'>
                <div className='flex items-center'>
                  {subtitle1}
                  <p className=' text-wfGrey900 ml-1'>{subtitle2}</p>
                </div>
              </div>
              <div className=' text-wfGrey700 mt-1 flex justify-center'>
                {text}
              </div>
            </div>
          </div>
        }
        onConfirm={async () => {
          setInnerLoading(true);
          await keepTypeMap[keepType]();
          setInnerLoading(false);
          closeConfirmModal();
        }}
        onClose={() => {
          closeConfirmModal();
        }}
      />
    ),
  };
};
